/**
 * ContributionTile.tsx
 *
 * Renders a tile for a contribution action (e.g. create recipe, read guide, get help).
 *
 * Props:
 * - icon: React.ReactNode - Icon to display in the tile.
 * - label: string - Label text for the tile.
 * - component?: any - Optional custom component type for the tile.
 * - to?: string - Optional route for navigation (used with component).
 * - href?: string - Optional external link for the tile.
 * - target?: string - Optional target for the link.
 * - onClick?: () => void - Optional click handler.
 * - sx?: SxProps - Optional style overrides.
 */

import React from "react";
import { Box, Typography, SxProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ContributionTileProps {
  icon: React.ReactNode;
  label: string;
  component?: any;
  to?: string;
  href?: string;
  target?: string;
  onClick?: () => void;
  sx?: SxProps;
}

/**
 * ContributionTile component.
 * Renders a styled tile with icon and label, supporting navigation or click actions.
 */
const ContributionTile: React.FC<ContributionTileProps> = ({
  icon,
  label,
  component,
  to,
  href,
  target,
  onClick,
  sx,
}) => {
  const theme = useTheme();
  return (
    <Box
      component={component || (href ? "a" : "div")}
      to={to}
      href={href}
      target={target}
      onClick={onClick}
      sx={{
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        p: 3,
        textAlign: "left",
        minHeight: 120,
        gap: 2,
        cursor: "pointer",
        borderRadius: 2,
        textDecoration: "none",
        color: theme.palette.materiom.seaweed,
        fontWeight: "bold",
        bgcolor: theme.palette.grey[100],
        border: "1px solid transparent",
        transition: "border-color 0.3s",
        "&:hover": { borderColor: theme.palette.materiom.seaweed },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",

          borderRadius: "inherit",
          zIndex: -1,
        },
        ...sx,
      }}
    >
      {/* Icon container */}
      <Box
        sx={{
          bgcolor: "white",
          borderRadius: "50%",
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: "60%", height: "60%" }}>
          {/* Render icon, resizing if it's a React element */}
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<any>, {
                style: { width: "100%", height: "100%" },
              })
            : icon}
        </Box>
      </Box>
      {/* Tile label */}
      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
        {label}
      </Typography>
    </Box>
  );
};

export default ContributionTile;
